import React, { useEffect, useState, useRef, useCallback } from 'react';
import AdminLayout from "../UI/Layout/AdminLayout";
import Title from "../UI/Text/Title";
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faTimes, faChevronLeft, faChevronRight,
  faSpinner, faMobileAlt, faClock, faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import Axios from '../../axios/Axios';
import { HOST_URL_DB2, NODE_HOST_URL, NODE_HOST_URL_v1 } from '../Dashboard/api/Api';
import { userloginDetails } from '../Utils/AdminAuth';

const STATUS_CONFIG = {
  delivered: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  failed: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  sent: { color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  read: { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  default: { color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
};

const getStatusStyle = (status) =>
  STATUS_CONFIG[status ? status.toLowerCase() : ""] || STATUS_CONFIG.default;

const AdminSmsDetailReport = () => {
  const lStorage = userloginDetails();
  const userId = lStorage && lStorage.user ? lStorage.user.userId : null;
  const userToken = lStorage ? lStorage.token : null;

  const { id, totalData, username } = useParams();
  const totalDataCount = parseInt(totalData, 10) || 0;
  const perPageRef = useRef(10);

  const [loading, setLoading] = useState(false);
  const [smsReportList, setSmsReportList] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(totalDataCount);
  const [perPage, setPerPage] = useState(10);
  const [searchInput,  setSearchInput]  = useState("");
  const [searchQuery,  setSearchQuery]  = useState("");
  const [windowWidth,  setWindowWidth]  = useState(typeof window !== "undefined" ? window.innerWidth : 1200);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = windowWidth < 640;
  const isTablet = windowWidth >= 640 && windowWidth < 1024;

  const fetchReport = useCallback(async (page) => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("user_id", userId);
      formData.append("method", "report");
      formData.append("token", userToken);
      formData.append("channel", "sms");
      formData.append("campaign_id", id);
      formData.append("page_no", page);

      const { data } = await Axios.post(`${HOST_URL_DB2}/bulk_campaign_sms.php`, formData);

      if (data.success) {
        const records = Array.isArray(data.data) ? data.data : [];
        if (page === 1 && records.length > 0) {
          perPageRef.current = records.length;
          setPerPage(records.length);
        }
        const pp = perPageRef.current;
        const pages = totalDataCount > 0 && pp > 0 ? Math.ceil(totalDataCount / pp) : 1;
        setSmsReportList(records);
        setTotalRecords(totalDataCount);
        setTotalPages(Math.max(1, pages));
        setCurrentPage(page);
      } else {
        setError(data.message || "Failed to load data.");
      }
    } catch (err) {
      setError(err.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [userId, userToken, id, totalDataCount]);

  useEffect(() => {
    fetchReport(1);
  }, [fetchReport]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchReport(page);
  };

  const filterdata = async () => {
    if (!searchInput.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        user_id: userId,
        method: "NumberSearch",
        number: searchInput,
        campaign_id: parseInt(id),
        username: username,
        filter : "smsSearch",
        token: userToken,
      };
      const { data } = await Axios.post(`${NODE_HOST_URL_v1}/admin/CampaignSearch`, payload);
      if (data.success === true) {
        setSmsReportList(Array.isArray(data.data) ? data.data : []);
        setSearchQuery(searchInput.trim());
      } else {
        setSmsReportList([]);
        setSearchQuery(searchInput.trim());
        setError(data.message || "No results found.");
      }
    } catch (err) {
      setError(err.response.data.message || "Network error during search.");
    } finally {
      setLoading(false);
    }
  };

  const resetFilter = () => {
    setSearchInput("");
    setSearchQuery("");
    setError(null);
    fetchReport(1);
  };

  const renderPagePills = () => {
    if (totalPages <= 1) return null;
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
      .reduce((acc, p, idx, arr) => {
        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
        acc.push(p);
        return acc;
      }, []);

    return pages.map((p, idx) =>
      p === "..." ? (
        <span key={"e" + idx} style={{ padding: "0 6px", color: "#94a3b8", fontSize: "13px" }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => handlePageChange(p)}
          style={{
            minWidth: "34px", height: "34px",
            border: "1px solid",
            borderColor: currentPage === p ? "#4f46e5" : "#e2e8f0",
            background: currentPage === p ? "#4f46e5" : "#fff",
            color: currentPage === p ? "#fff" : "#475569",
            borderRadius: "8px", fontSize: "13px",
            fontWeight: currentPage === p ? "700" : "400",
            cursor: "pointer",
          }}
        >
          {p}
        </button>
      )
    );
  };

  const [tooltip, setTooltip] = useState({ visible: false, content: "", x: 0, y: 0 });

  const showTooltip = (e, content) => {
    if (!content) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ visible: true, content, x: rect.left + window.scrollX, y: rect.bottom + window.scrollY + 6 });
  };

  const hideTooltip = () => setTooltip(t => ({ ...t, visible: false }));

  const COLUMNS = ["Mobile Number", "Content", "Submitted On", "Delivered On", "Status", "Count", "Country", "Error Code", "Route"];

  return (
    <>
    {tooltip.visible && (
      <div style={{
        position: "absolute",
        top: tooltip.y,
        left: tooltip.x,
        zIndex: 9999,
        maxWidth: "340px",
        background: "#1e293b",
        color: "#f1f5f9",
        padding: "10px 14px",
        borderRadius: "10px",
        fontSize: "12px",
        lineHeight: "1.6",
        boxShadow: "0 8px 24px rgba(0,0,0,0.22)",
        wordBreak: "break-word",
        pointerEvents: "none",
      }}>
        {tooltip.content}
      </div>
    )}
    <AdminLayout>
      <main className="content">
        <div className="container-fluid p-0">
          <Title isButton={false}>Campaign Report</Title>

          {error && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: "12px", padding: isMobile ? "12px 14px" : "14px 18px",
              marginBottom: "16px",
            }}>
              <span style={{ fontSize: "18px", lineHeight: 1 }}>&#9888;</span>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#dc2626", marginBottom: "2px" }}>{error}</div>
              </div>
              <button
                onClick={() => setError(null)}
                style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#f87171", fontSize: "16px", lineHeight: 1, padding: 0 }}
              >
                &#x2715;
              </button>
            </div>
          )}

          <div style={{
            background: "#fff", borderRadius: isMobile ? "12px" : "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            fontFamily: "Inter, system-ui, sans-serif",
            overflow: "hidden",
          }}>
            {/* ── Header ── */}
            <div style={{
              padding: isMobile ? "14px 16px" : "20px 24px",
              borderBottom: "1px solid #f1f5f9",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? "12px" : "0",
            }}>
              <h3 style={{ margin: 0, fontSize: isMobile ? "15px" : "18px", fontWeight: "800", color: "#0f172a" }}>
                Campaign ID: <span style={{ color: "#4f46e5" }}>{id}</span>
              </h3>

              <div style={{ display: "flex", alignItems: "center", gap: "8px", width: isMobile ? "100%" : "auto" }}>
                <div style={{ position: "relative", flex: 1 }}>
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "12px", pointerEvents: "none" }}
                  />
                  <input
                    type="text"
                    placeholder="Search mobile number..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") filterdata(); }}
                    style={{
                      width: "100%",
                      paddingLeft: "30px", paddingRight: searchInput ? "30px" : "12px",
                      height: "36px", border: "1px solid #e2e8f0", borderRadius: "8px",
                      fontSize: "13px", color: "#334155", outline: "none",
                      background: "#f8fafc", boxSizing: "border-box",
                    }}
                  />
                  {searchInput && (
                    <button
                      onClick={resetFilter}
                      style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, color: "#94a3b8", fontSize: "11px" }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
                <button
                  onClick={filterdata}
                  style={{
                    height: "36px", padding: "0 14px",
                    background: "#4f46e5", color: "#fff",
                    border: "none", borderRadius: "8px",
                    fontSize: "13px", fontWeight: "600", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "6px",
                    whiteSpace: "nowrap", flexShrink: 0,
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} style={{ fontSize: "11px" }} />
                  {!isMobile && " Search"}
                </button>
              </div>
            </div>

            {/* ── Table ── */}
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: isMobile ? "48px 0" : "80px 0", flexDirection: "column", gap: "12px" }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "28px", color: "#4f46e5" }} />
                <span style={{ color: "#64748b", fontSize: "13px" }}>Loading records…</span>
              </div>
            ) : smsReportList.length === 0 ? (
              <div style={{ textAlign: "center", padding: isMobile ? "48px 16px" : "80px 0", color: "#94a3b8" }}>
                <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: "36px", marginBottom: "12px", opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: "14px" }}>No records found.</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
                  <thead style={{ background: "#f8fafc" }}>
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      {COLUMNS.map((h, i) => (
                        <th key={i} style={{
                          padding: "12px 16px", fontSize: "11px", fontWeight: "700",
                          color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px",
                          textAlign: "left", whiteSpace: "nowrap",
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {smsReportList.map((item, i) => {
                      const sts = getStatusStyle(item.status);
                      return (
                        <tr
                          key={i}
                          style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          {/* Mobile */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FontAwesomeIcon icon={faMobileAlt} style={{ color: "#6366f1", fontSize: "13px" }} />
                              <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>{item.mobile || item.phone_number}</span>
                            </div>
                          </td>

                          {/* Content */}
                          <td style={{ padding: "14px 16px", maxWidth: "200px" }}>
                            <span
                              onMouseEnter={e => showTooltip(e, item.content || item.campaign_message)}
                              onMouseLeave={hideTooltip}
                              style={{
                                fontSize: "12px", color: "#475569",
                                display: "-webkit-box", WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word",
                                cursor: "default",
                              }}
                            >
                              {item.content || item.campaign_message}
                            </span>
                          </td>

                          {/* Submitted On */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <FontAwesomeIcon icon={faClock} style={{ color: "#94a3b8", fontSize: "11px" }} />
                              <span style={{ fontSize: "12px", color: "#475569" }}>{item.sent_time || "—"}</span>
                            </div>
                          </td>

                          {/* Delivered On */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <FontAwesomeIcon
                                icon={item.deliver_time ? faCheckCircle : faClock}
                                style={{ color: item.deliver_time ? "#16a34a" : "#cbd5e1", fontSize: "11px" }}
                              />
                              <span style={{ fontSize: "12px", color: "#475569" }}>{item.deliver_time || "—"}</span>
                            </div>
                          </td>

                          {/* Status */}
                          <td style={{ padding: "14px 16px" }}>
                            <span style={{
                              background: sts.bg, color: sts.color,
                              border: "1px solid " + sts.border,
                              padding: "3px 10px", borderRadius: "100px",
                              fontSize: "11px", fontWeight: "700",
                              textTransform: "capitalize", whiteSpace: "nowrap",
                            }}>
                              {item.status || "—"}
                            </span>
                          </td>

                          {/* Count */}
                          <td style={{ padding: "14px 16px", fontSize: "12px", color: "#475569", textAlign: "center" }}>
                            {item.msg_count || "—"}
                          </td>

                          {/* Country */}
                          <td style={{ padding: "14px 16px", fontSize: "12px", color: "#475569" }}>
                            {item.countryname || item.country_code || "—"}
                          </td>

                          {/* Error Code */}
                          <td style={{ padding: "14px 16px", maxWidth: "160px" }}>
                            <span style={{
                              fontSize: "12px", color: "#475569",
                              display: "-webkit-box", WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word",
                            }}>
                              {item.api_status || "—"}
                            </span>
                          </td>

                          {/* Route */}
                          <td style={{ padding: "14px 16px", fontSize: "12px", color: "#475569" }}>
                            {item.route || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Pagination ── */}
            {!loading && smsReportList.length > 0 && !searchQuery && (
              <div style={{
                padding: isMobile ? "12px 16px" : "16px 24px",
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: "10px",
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {totalRecords > 0 ? (
                    <span style={{ fontSize: isMobile ? "12px" : "13px", color: "#334155", fontWeight: "600" }}>
                      Showing{" "}
                      <span style={{ color: "#4f46e5" }}>{(currentPage - 1) * perPage + 1}</span>
                      {" – "}
                      <span style={{ color: "#4f46e5" }}>{Math.min(currentPage * perPage, totalRecords)}</span>
                      {" of "}
                      <span style={{ color: "#4f46e5" }}>{totalRecords}</span>
                      {" records"}
                      {!isMobile && (
                        <span style={{ color: "#94a3b8", fontWeight: "400", fontSize: "12px", marginLeft: "6px" }}>
                          (Page {currentPage} of {totalPages})
                        </span>
                      )}
                    </span>
                  ) : (
                    <span style={{ fontSize: "13px", color: "#64748b" }}>
                      Page <b style={{ color: "#4f46e5" }}>{currentPage}</b>
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: isMobile ? "center" : "flex-end", flexWrap: "wrap" }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      height: isMobile ? "32px" : "34px", padding: "0 12px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: currentPage === 1 ? "#f8fafc" : "#fff",
                      color: currentPage === 1 ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "10px" }} /> Prev
                  </button>

                  {!isMobile && renderPagePills()}

                  {isMobile && (
                    <span style={{ fontSize: "13px", color: "#64748b", padding: "0 8px" }}>
                      {currentPage} / {totalPages}
                    </span>
                  )}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    style={{
                      height: isMobile ? "32px" : "34px", padding: "0 12px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: currentPage >= totalPages ? "#f8fafc" : "#fff",
                      color: currentPage >= totalPages ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "10px" }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </AdminLayout>
    </>
  );
};

export default AdminSmsDetailReport;
